import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  ComplianceRecord,
  ComplianceType,
  ComplianceStatus,
  Policy,
  Audit,
  AuditType,
  AuditStatus,
  ComplianceIncident,
  IncidentType,
  IncidentSeverity,
  IncidentStatus,
  HIPAAComplianceLog,
  BusinessAssociateAgreement,
} from '@/models/compliance.model';

const COMPLIANCE_RECORDS_COLLECTION = 'complianceRecords';
const POLICIES_COLLECTION = 'policies';
const AUDITS_COLLECTION = 'audits';
const INCIDENTS_COLLECTION = 'complianceIncidents';
const HIPAA_LOGS_COLLECTION = 'hipaaComplianceLogs';
const BAA_COLLECTION = 'businessAssociateAgreements';

export class ComplianceService {
  // ========== Compliance Records ==========

  static async getAllComplianceRecords(): Promise<ComplianceRecord[]> {
    const q = query(
      collection(db, COMPLIANCE_RECORDS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToComplianceRecord(doc.id, doc.data()));
  }

  static async getComplianceRecordById(id: string): Promise<ComplianceRecord | null> {
    const docRef = doc(db, COMPLIANCE_RECORDS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    return this.docToComplianceRecord(docSnap.id, docSnap.data());
  }

  static async filterComplianceRecordsByType(type: ComplianceType): Promise<ComplianceRecord[]> {
    const q = query(
      collection(db, COMPLIANCE_RECORDS_COLLECTION),
      where('type', '==', type),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToComplianceRecord(doc.id, doc.data()));
  }

  static async filterComplianceRecordsByStatus(status: ComplianceStatus): Promise<ComplianceRecord[]> {
    const q = query(
      collection(db, COMPLIANCE_RECORDS_COLLECTION),
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToComplianceRecord(doc.id, doc.data()));
  }

  static async createComplianceRecord(recordData: Partial<ComplianceRecord>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      type: recordData.type || ComplianceType.other,
      name: recordData.name || '',
      description: recordData.description || null,
      status: recordData.status || ComplianceStatus.pending,
      requirement: recordData.requirement || null,
      applicableTo: recordData.applicableTo || [],
      dueDate: recordData.dueDate ? Timestamp.fromDate(recordData.dueDate) : null,
      completedDate: recordData.completedDate ? Timestamp.fromDate(recordData.completedDate) : null,
      renewalDate: recordData.renewalDate ? Timestamp.fromDate(recordData.renewalDate) : null,
      responsibleParty: recordData.responsibleParty || null,
      documents: recordData.documents || [],
      notes: recordData.notes || null,
      createdAt: now,
      lastUpdated: now,
    };

    const docRef = await addDoc(collection(db, COMPLIANCE_RECORDS_COLLECTION), data);
    return docRef.id;
  }

  static async updateComplianceRecord(
    id: string,
    updates: Partial<ComplianceRecord>
  ): Promise<void> {
    const docRef = doc(db, COMPLIANCE_RECORDS_COLLECTION, id);
    const updateData: any = {
      lastUpdated: Timestamp.now(),
    };

    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.completedDate !== undefined) updateData.completedDate = updates.completedDate ? Timestamp.fromDate(updates.completedDate) : null;
    if (updates.renewalDate !== undefined) updateData.renewalDate = updates.renewalDate ? Timestamp.fromDate(updates.renewalDate) : null;
    if (updates.notes !== undefined) updateData.notes = updates.notes;
    if (updates.documents !== undefined) updateData.documents = updates.documents;

    await updateDoc(docRef, updateData);
  }

  // ========== Policies ==========

  static async getAllPolicies(): Promise<Policy[]> {
    const q = query(
      collection(db, POLICIES_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToPolicy(doc.id, doc.data()));
  }

  static async getPolicyById(id: string): Promise<Policy | null> {
    const docRef = doc(db, POLICIES_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    return this.docToPolicy(docSnap.id, docSnap.data());
  }

  static async getActivePolicies(): Promise<Policy[]> {
    const q = query(
      collection(db, POLICIES_COLLECTION),
      where('status', '==', 'active'),
      orderBy('effectiveDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToPolicy(doc.id, doc.data()));
  }

  static async createPolicy(policyData: Partial<Policy>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      title: policyData.title || '',
      category: policyData.category || 'other',
      content: policyData.content || '',
      version: policyData.version || 1,
      effectiveDate: policyData.effectiveDate ? Timestamp.fromDate(policyData.effectiveDate) : now,
      expirationDate: policyData.expirationDate ? Timestamp.fromDate(policyData.expirationDate) : null,
      status: policyData.status || 'draft',
      requiresAcknowledgment: policyData.requiresAcknowledgment || false,
      acknowledgmentDeadline: policyData.acknowledgmentDeadline ? Timestamp.fromDate(policyData.acknowledgmentDeadline) : null,
      createdBy: policyData.createdBy || '',
      createdByName: policyData.createdByName || '',
      approvedBy: policyData.approvedBy || null,
      approvedByName: policyData.approvedByName || null,
      approvedAt: policyData.approvedAt ? Timestamp.fromDate(policyData.approvedAt) : null,
      acknowledgments: policyData.acknowledgments || [],
      createdAt: now,
      lastUpdated: now,
    };

    const docRef = await addDoc(collection(db, POLICIES_COLLECTION), data);
    return docRef.id;
  }

  static async updatePolicy(id: string, updates: Partial<Policy>): Promise<void> {
    const docRef = doc(db, POLICIES_COLLECTION, id);
    const updateData: any = {
      lastUpdated: Timestamp.now(),
    };

    if (updates.title !== undefined) updateData.title = updates.title;
    if (updates.category !== undefined) updateData.category = updates.category;
    if (updates.content !== undefined) updateData.content = updates.content;
    if (updates.version !== undefined) updateData.version = updates.version;
    if (updates.effectiveDate !== undefined) updateData.effectiveDate = updates.effectiveDate ? Timestamp.fromDate(updates.effectiveDate) : null;
    if (updates.expirationDate !== undefined) updateData.expirationDate = updates.expirationDate ? Timestamp.fromDate(updates.expirationDate) : null;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.requiresAcknowledgment !== undefined) updateData.requiresAcknowledgment = updates.requiresAcknowledgment;
    if (updates.acknowledgmentDeadline !== undefined) updateData.acknowledgmentDeadline = updates.acknowledgmentDeadline ? Timestamp.fromDate(updates.acknowledgmentDeadline) : null;
    if (updates.approvedBy !== undefined) updateData.approvedBy = updates.approvedBy;
    if (updates.approvedByName !== undefined) updateData.approvedByName = updates.approvedByName;
    if (updates.approvedAt !== undefined) updateData.approvedAt = updates.approvedAt ? Timestamp.fromDate(updates.approvedAt) : null;

    await updateDoc(docRef, updateData);
  }

  // ========== Audits ==========

  static async getAllAudits(): Promise<Audit[]> {
    const q = query(
      collection(db, AUDITS_COLLECTION),
      orderBy('scheduledDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToAudit(doc.id, doc.data()));
  }

  static async getAuditById(id: string): Promise<Audit | null> {
    const docRef = doc(db, AUDITS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    return this.docToAudit(docSnap.id, docSnap.data());
  }

  static async createAudit(auditData: Partial<Audit>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      type: auditData.type || AuditType.internal,
      name: auditData.name || '',
      description: auditData.description || null,
      status: auditData.status || AuditStatus.scheduled,
      scheduledDate: auditData.scheduledDate ? Timestamp.fromDate(auditData.scheduledDate) : now,
      startDate: auditData.startDate ? Timestamp.fromDate(auditData.startDate) : null,
      endDate: auditData.endDate ? Timestamp.fromDate(auditData.endDate) : null,
      conductedBy: auditData.conductedBy || null,
      conductedByName: auditData.conductedByName || null,
      scope: auditData.scope || null,
      findings: auditData.findings || [],
      recommendations: auditData.recommendations || [],
      reportUrl: auditData.reportUrl || null,
      createdAt: now,
      lastUpdated: now,
    };

    const docRef = await addDoc(collection(db, AUDITS_COLLECTION), data);
    return docRef.id;
  }

  static async updateAudit(id: string, updates: Partial<Audit>): Promise<void> {
    const docRef = doc(db, AUDITS_COLLECTION, id);
    const updateData: any = {
      lastUpdated: Timestamp.now(),
    };

    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.startDate !== undefined) updateData.startDate = updates.startDate ? Timestamp.fromDate(updates.startDate) : null;
    if (updates.endDate !== undefined) updateData.endDate = updates.endDate ? Timestamp.fromDate(updates.endDate) : null;
    if (updates.conductedBy !== undefined) updateData.conductedBy = updates.conductedBy;
    if (updates.conductedByName !== undefined) updateData.conductedByName = updates.conductedByName;
    if (updates.scope !== undefined) updateData.scope = updates.scope;
    if (updates.findings !== undefined) updateData.findings = updates.findings;
    if (updates.recommendations !== undefined) updateData.recommendations = updates.recommendations;
    if (updates.reportUrl !== undefined) updateData.reportUrl = updates.reportUrl;

    await updateDoc(docRef, updateData);
  }

  // ========== Incidents ==========

  static async getAllIncidents(): Promise<ComplianceIncident[]> {
    const q = query(
      collection(db, INCIDENTS_COLLECTION),
      orderBy('reportedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToIncident(doc.id, doc.data()));
  }

  static async getIncidentById(id: string): Promise<ComplianceIncident | null> {
    const docRef = doc(db, INCIDENTS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    return this.docToIncident(docSnap.id, docSnap.data());
  }

  static async createIncident(incidentData: Partial<ComplianceIncident>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      type: incidentData.type || IncidentType.other,
      severity: incidentData.severity || IncidentSeverity.medium,
      status: incidentData.status || IncidentStatus.reported,
      title: incidentData.title || '',
      description: incidentData.description || '',
      reportedBy: incidentData.reportedBy || '',
      reportedByName: incidentData.reportedByName || '',
      reportedAt: now,
      affectedEntities: incidentData.affectedEntities || [],
      affectedData: incidentData.affectedData || null,
      discoveredAt: incidentData.discoveredAt ? Timestamp.fromDate(incidentData.discoveredAt) : now,
      containedAt: incidentData.containedAt ? Timestamp.fromDate(incidentData.containedAt) : null,
      resolvedAt: incidentData.resolvedAt ? Timestamp.fromDate(incidentData.resolvedAt) : null,
      investigationNotes: incidentData.investigationNotes || null,
      remediationSteps: incidentData.remediationSteps || [],
      notificationRequired: incidentData.notificationRequired || false,
      notifiedAt: incidentData.notifiedAt ? Timestamp.fromDate(incidentData.notifiedAt) : null,
      notificationRecipients: incidentData.notificationRecipients || [],
      followUpRequired: incidentData.followUpRequired || false,
      followUpDate: incidentData.followUpDate ? Timestamp.fromDate(incidentData.followUpDate) : null,
      createdAt: now,
      lastUpdated: now,
    };

    const docRef = await addDoc(collection(db, INCIDENTS_COLLECTION), data);
    return docRef.id;
  }

  static async updateIncident(
    id: string,
    updates: Partial<ComplianceIncident>
  ): Promise<void> {
    const docRef = doc(db, INCIDENTS_COLLECTION, id);
    const updateData: any = {
      lastUpdated: Timestamp.now(),
    };

    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.containedAt !== undefined) updateData.containedAt = updates.containedAt ? Timestamp.fromDate(updates.containedAt) : null;
    if (updates.resolvedAt !== undefined) updateData.resolvedAt = updates.resolvedAt ? Timestamp.fromDate(updates.resolvedAt) : null;
    if (updates.investigationNotes !== undefined) updateData.investigationNotes = updates.investigationNotes;
    if (updates.remediationSteps !== undefined) updateData.remediationSteps = updates.remediationSteps;

    await updateDoc(docRef, updateData);
  }

  // ========== HIPAA Logs ==========

  static async getHIPAALogs(limit: number = 100): Promise<HIPAAComplianceLog[]> {
    const q = query(
      collection(db, HIPAA_LOGS_COLLECTION),
      orderBy('timestamp', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.slice(0, limit).map(doc => this.docToHIPAALog(doc.id, doc.data()));
  }

  static async createHIPAALog(logData: Partial<HIPAAComplianceLog>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      userId: logData.userId || '',
      userName: logData.userName || '',
      action: logData.action || '',
      entityType: logData.entityType || '',
      entityId: logData.entityId || '',
      ipAddress: logData.ipAddress || null,
      timestamp: logData.timestamp ? Timestamp.fromDate(logData.timestamp) : now,
      reason: logData.reason || null,
      metadata: logData.metadata || {},
    };

    const docRef = await addDoc(collection(db, HIPAA_LOGS_COLLECTION), data);
    return docRef.id;
  }

  // ========== Business Associate Agreements ==========

  static async getAllBAAs(): Promise<BusinessAssociateAgreement[]> {
    const q = query(
      collection(db, BAA_COLLECTION),
      orderBy('agreementDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToBAA(doc.id, doc.data()));
  }

  static async createBAA(baaData: Partial<BusinessAssociateAgreement>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      vendorName: baaData.vendorName || '',
      vendorContact: baaData.vendorContact || null,
      agreementDate: baaData.agreementDate ? Timestamp.fromDate(baaData.agreementDate) : now,
      expirationDate: baaData.expirationDate ? Timestamp.fromDate(baaData.expirationDate) : null,
      status: baaData.status || 'active',
      documentUrl: baaData.documentUrl || '',
      signedBy: baaData.signedBy || '',
      signedByName: baaData.signedByName || '',
      notes: baaData.notes || null,
      createdAt: now,
      lastUpdated: now,
    };

    const docRef = await addDoc(collection(db, BAA_COLLECTION), data);
    return docRef.id;
  }

  static async updateBAA(id: string, updates: Partial<BusinessAssociateAgreement>): Promise<void> {
    const docRef = doc(db, BAA_COLLECTION, id);
    const updateData: any = {
      lastUpdated: Timestamp.now(),
    };

    if (updates.vendorName !== undefined) updateData.vendorName = updates.vendorName;
    if (updates.vendorContact !== undefined) updateData.vendorContact = updates.vendorContact;
    if (updates.agreementDate !== undefined) updateData.agreementDate = updates.agreementDate ? Timestamp.fromDate(updates.agreementDate) : null;
    if (updates.expirationDate !== undefined) updateData.expirationDate = updates.expirationDate ? Timestamp.fromDate(updates.expirationDate) : null;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.documentUrl !== undefined) updateData.documentUrl = updates.documentUrl;
    if (updates.notes !== undefined) updateData.notes = updates.notes;

    await updateDoc(docRef, updateData);
  }

  // ========== Helper Methods ==========

  private static docToComplianceRecord(id: string, data: any): ComplianceRecord {
    return {
      id,
      type: data.type || ComplianceType.other,
      name: data.name || '',
      description: data.description,
      status: data.status || ComplianceStatus.pending,
      requirement: data.requirement,
      applicableTo: data.applicableTo || [],
      dueDate: data.dueDate?.toDate(),
      completedDate: data.completedDate?.toDate(),
      renewalDate: data.renewalDate?.toDate(),
      responsibleParty: data.responsibleParty,
      documents: data.documents || [],
      notes: data.notes,
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
    };
  }

  private static docToPolicy(id: string, data: any): Policy {
    return {
      id,
      title: data.title || '',
      category: data.category || 'other',
      content: data.content || '',
      version: data.version || 1,
      effectiveDate: data.effectiveDate?.toDate() || new Date(),
      expirationDate: data.expirationDate?.toDate(),
      status: data.status || 'draft',
      requiresAcknowledgment: data.requiresAcknowledgment || false,
      acknowledgmentDeadline: data.acknowledgmentDeadline?.toDate(),
      createdBy: data.createdBy || '',
      createdByName: data.createdByName || '',
      approvedBy: data.approvedBy,
      approvedByName: data.approvedByName,
      approvedAt: data.approvedAt?.toDate(),
      acknowledgments: (data.acknowledgments || []).map((ack: any) => ({
        userId: ack.userId || '',
        userName: ack.userName || '',
        acknowledgedAt: ack.acknowledgedAt?.toDate() || new Date(),
        ipAddress: ack.ipAddress,
      })),
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
    };
  }

  private static docToAudit(id: string, data: any): Audit {
    return {
      id,
      type: data.type || AuditType.internal,
      name: data.name || '',
      description: data.description,
      status: data.status || AuditStatus.scheduled,
      scheduledDate: data.scheduledDate?.toDate() || new Date(),
      startDate: data.startDate?.toDate(),
      endDate: data.endDate?.toDate(),
      conductedBy: data.conductedBy,
      conductedByName: data.conductedByName,
      scope: data.scope,
      findings: (data.findings || []).map((finding: any) => ({
        id: finding.id || '',
        severity: finding.severity || 'medium',
        description: finding.description || '',
        recommendation: finding.recommendation,
        status: finding.status || 'open',
        assignedTo: finding.assignedTo,
        dueDate: finding.dueDate?.toDate(),
        resolvedDate: finding.resolvedDate?.toDate(),
        notes: finding.notes,
      })),
      recommendations: data.recommendations || [],
      reportUrl: data.reportUrl,
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
    };
  }

  private static docToIncident(id: string, data: any): ComplianceIncident {
    return {
      id,
      type: data.type || IncidentType.other,
      severity: data.severity || IncidentSeverity.medium,
      status: data.status || IncidentStatus.reported,
      title: data.title || '',
      description: data.description || '',
      reportedBy: data.reportedBy || '',
      reportedByName: data.reportedByName || '',
      reportedAt: data.reportedAt?.toDate() || new Date(),
      affectedEntities: data.affectedEntities || [],
      affectedData: data.affectedData,
      discoveredAt: data.discoveredAt?.toDate() || new Date(),
      containedAt: data.containedAt?.toDate(),
      resolvedAt: data.resolvedAt?.toDate(),
      investigationNotes: data.investigationNotes,
      remediationSteps: data.remediationSteps || [],
      notificationRequired: data.notificationRequired || false,
      notifiedAt: data.notifiedAt?.toDate(),
      notificationRecipients: data.notificationRecipients || [],
      followUpRequired: data.followUpRequired || false,
      followUpDate: data.followUpDate?.toDate(),
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
    };
  }

  private static docToHIPAALog(id: string, data: any): HIPAAComplianceLog {
    return {
      id,
      userId: data.userId || '',
      userName: data.userName || '',
      action: data.action || '',
      entityType: data.entityType || '',
      entityId: data.entityId || '',
      ipAddress: data.ipAddress,
      timestamp: data.timestamp?.toDate() || new Date(),
      reason: data.reason,
      metadata: data.metadata || {},
    };
  }

  private static docToBAA(id: string, data: any): BusinessAssociateAgreement {
    return {
      id,
      vendorName: data.vendorName || '',
      vendorContact: data.vendorContact,
      agreementDate: data.agreementDate?.toDate() || new Date(),
      expirationDate: data.expirationDate?.toDate(),
      status: data.status || 'active',
      documentUrl: data.documentUrl || '',
      signedBy: data.signedBy || '',
      signedByName: data.signedByName || '',
      notes: data.notes,
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
    };
  }
}

